import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Download } from "react-feather";
import {
  DashboardRole,
  DashboardCategory,
  DashboardGranularity,
  HeatmapResponse,
  HeatmapCell,
  HeatmapCountries,
  MetricGroup,
  TeacherGridResponse,
  TeacherGridRow,
  getDashboardSlotHeatmap,
  getDashboardSlotHeatmapTeachers,
} from "../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import {
  Badge,
  Button,
  Card,
  Disclosure,
  EmptyState,
  Field,
  Loader,
  Select,
  Table,
  TextInput,
  Toolbar,
} from "../../../../ui";
import SegmentedControl from "./ui-pending/SegmentedControl";
import {
  HmMetric,
  aeCountCellClass,
  bucketTone,
  fmtPct,
  metricCellClass,
  roleTag,
  studentBadge,
  dashboardErrorMessage,
  validateRange,
} from "./slotHelpers";

type HmMode = "category" | "person";

const COUNTRY_ROWS: { key: keyof HeatmapCountries; label: string; color: string }[] = [
  { key: "india", label: "India", color: "#e8671a" },
  { key: "us", label: "US", color: "#2563eb" },
  { key: "row", label: "ROW", color: "#0891b2" },
];

const CountryBreakdown: React.FC<{ countries?: HeatmapCountries | null }> = ({ countries }) => {
  if (!countries) return null;
  return (
    <div className="sa-heat-tip__countries">
      <div className="sa-heat-tip__country-head">
        <span />
        <span>Booked</span>
        <span>Conduct</span>
      </div>
      {COUNTRY_ROWS.map(({ key, label, color }) => {
        const m = countries[key];
        return (
          <div className="sa-heat-tip__country" key={key}>
            <span style={{ color }}>{label}</span>
            <span>{m?.booked ?? 0}</span>
            <span>{fmtPct(m?.conductPct)}</span>
          </div>
        );
      })}
    </div>
  );
};

const MetricLines: React.FC<{ m: MetricGroup; withAeTe?: { ae?: MetricGroup; te?: MetricGroup }; roleAll?: boolean }> = ({ m, withAeTe, roleAll }) => (
  <>
    <div className="sa-heat-tip__row"><span>Slots available</span><b style={{ color: "#3d52d5" }}>{m.available}</b></div>
    {roleAll && withAeTe?.ae && withAeTe.ae.available > 0 && <div className="sa-heat-tip__row"><span>AE</span><b style={{ color: "#3d52d5" }}>{withAeTe.ae.available}</b></div>}
    {roleAll && withAeTe?.te && withAeTe.te.available > 0 && <div className="sa-heat-tip__row"><span>TE</span><b style={{ color: "#0b8a6e" }}>{withAeTe.te.available}</b></div>}
    <div className="sa-heat-tip__row"><span>Slots booked</span><b>{m.booked}</b></div>
    <div className="sa-heat-tip__row"><span>Conducted</span><b>{m.conducted}</b></div>
    <div className="sa-heat-tip__row"><span>Booked %</span><b>{fmtPct(m.bookedPct)}</b></div>
    <div className="sa-heat-tip__row"><span>Conduct %</span><b style={{ color: "#3d52d5" }}>{fmtPct(m.conductPct)}</b></div>
  </>
);

/** Hover card drawn on the document so the grid's overflow doesn't clip it. */
const HeatTip: React.FC<{ children: React.ReactNode; tip: React.ReactNode; className?: string }> = ({
  children,
  tip,
  className,
}) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: "hidden" });

  const place = useCallback(() => {
    const cell = cellRef.current;
    const card = tipRef.current;
    if (!cell || !card) return;
    const rect = cell.getBoundingClientRect();
    const height = card.offsetHeight;
    const width = card.offsetWidth;
    const aboveTop = rect.top - height - 10;
    const top = aboveTop > 8 ? aboveTop : Math.min(rect.bottom + 10, window.innerHeight - height - 8);
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.min(Math.max(left, 8), window.innerWidth - width - 8);
    setStyle({ position: "fixed", top, left, visibility: "visible", zIndex: 2000 });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place, tip]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  return (
    <td
      ref={cellRef}
      className={["tt-wrap", className].filter(Boolean).join(" ")}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {open && createPortal(
        <div ref={tipRef} className="sa-heat-tip" style={style}>{tip}</div>,
        document.body,
      )}
    </td>
  );
};

const today = new Date();
const iso = (d: Date) => d.toISOString().slice(0, 10);
const DEFAULT_TO = iso(today);
const DEFAULT_FROM = iso(today); // default to the current day

/** Legend + cell-key copy per metric mode. Swatches reuse the heatmap cell classes. */
const LEGEND: Record<HmMetric, { swatches: { cls: string; label: string }[]; note?: string; cellKey: string }> = {
  booked: {
    swatches: [
      { cls: "hb-green", label: "<60% booked" },
      { cls: "hb-amber", label: "60–79%" },
      { cls: "hb-red", label: "80%+ booked" },
    ],
    cellKey: "booked / avail · booked %",
  },
  conduct: {
    swatches: [
      { cls: "hc-crit", label: "<30% conduct" },
      { cls: "hc-warn", label: "30–49%" },
      { cls: "hc-good", label: "50%+ conduct" },
      { cls: "hc-nobook", label: "No bookings" },
    ],
    cellKey: "conducted / booked · conduct %",
  },
  aecount: {
    swatches: [],
    note: "Total AE+TE available per slot · hover for AE / TE breakdown",
    cellKey: "total available (hover for AE/TE split)",
  },
};

/** Slot-presence chip for the collapsed people×window grid. */
const slotChip = (slot: "intl" | "dom" | "both" | null | undefined) => {
  if (slot === "both") return <Badge tone="primary">both</Badge>;
  if (slot === "intl") return <Badge tone="info"><span aria-label="international">✈</span></Badge>;
  if (slot === "dom") return <Badge tone="warning"><span aria-label="domestic">⌂</span></Badge>;
  return <></>;
};

/** The two numeric lines inside a cell, per metric mode. */
const cellBody = (metric: HmMetric, m: MetricGroup) =>
  metric === "booked" ? (
    <>
      <div className="hm-cell-ratio">{m.booked}/{m.available}</div>
      <div className="hm-cell-conduct">{fmtPct(m.bookedPct)}</div>
    </>
  ) : (
    <>
      <div className="hm-cell-ratio">{m.conducted}/{m.booked}</div>
      <div className="hm-cell-conduct">{fmtPct(m.conductPct)}</div>
    </>
  );

const SlotHeatmap: React.FC = () => {
  const [mode, setMode] = useState<HmMode>("category");
  const [role, setRole] = useState<DashboardRole>("all");
  const [detail, setDetail] = useState<DashboardGranularity>("hourly");
  const [metric, setMetric] = useState<HmMetric>("booked");
  const [from, setFrom] = useState<string>(DEFAULT_FROM);
  const [to, setTo] = useState<string>(DEFAULT_TO);
  const [categoryId, setCategoryId] = useState<string>("all");
  const [teacherId, setTeacherId] = useState<string>("all");

  const [heatmap, setHeatmap] = useState<HeatmapResponse | null>(null);
  // The response echoes back only the categories that were requested, so a filtered load would
  // otherwise shrink the dropdown to the one selected category. Keep the unfiltered list.
  const [allCategories, setAllCategories] = useState<DashboardCategory[]>([]);
  const [grid, setGrid] = useState<TeacherGridResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsedOpen, setCollapsedOpen] = useState<boolean>(false);

  // Day-wise has no per-window grid to colour, so AE/TE Count can only be shown hourly.
  const daywiseDisabled = metric === "aecount";
  const effectiveDetail: DashboardGranularity = daywiseDisabled ? "hourly" : detail;

  // Both endpoints are fetched every load: the aggregate heatmap drives the hourly/day-wise
  // grid, and the teacher grid drives the collapsed people×window detail, the person filter
  // and the per-window AE/TE headcounts.
  const fetchData = useCallback(async () => {
    const rangeErr = validateRange(from, to);
    if (rangeErr) {
      setError(rangeErr);
      toast.warning(rangeErr);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const catFilter = categoryId === "all" ? [] : [Number(categoryId)];
      const [hm, tg] = await Promise.all([
        getDashboardSlotHeatmap({ from, to, role, granularity: effectiveDetail, categoryId: catFilter }),
        getDashboardSlotHeatmapTeachers({ from, to, role, categoryId: catFilter }),
      ]);
      setHeatmap(hm);
      setGrid(tg);
      if (categoryId === "all" && hm.categories?.length) setAllCategories(hm.categories);
      trackEvent(MixpanelEvents.SLOT_ANALYSIS_HEATMAP_LOADED, { mode, role, detail: effectiveDetail, metric, from, to });
    } catch (err) {
      const msg = dashboardErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [mode, role, effectiveDetail, metric, from, to, categoryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categories = allCategories;
  const teachers = grid?.teachers ?? [];

  // People set powering the collapsed detail + chips (all filtered teachers, or one in person mode).
  const people: TeacherGridRow[] = useMemo(() => {
    if (mode === "person" && teacherId !== "all") {
      return teachers.filter((t) => String(t.teacherId) === teacherId);
    }
    return teachers;
  }, [mode, teacherId, teachers]);

  const title = useMemo(() => {
    if (mode === "person") {
      const t = teachers.find((x) => String(x.teacherId) === teacherId);
      return `${t ? t.name : "All people"} · Slot Heatmap`;
    }
    const c = categories.find((x) => String(x.id) === categoryId);
    return `${c ? c.name : "All Categories"} · Slot Heatmap`;
  }, [mode, teacherId, categoryId, teachers, categories]);

  const subtitle =
    metric === "booked"
      ? "Each cell: booked / available (booked %) · colour = capacity fill · hover for detail"
      : metric === "conduct"
        ? "Each cell: conducted / booked (conduct %) · colour = conduct band · hover for detail"
        : "Each cell: AE + TE available in the window · hover for the role split";

  // Day-wise carries no per-window rows, so it exports its own shape: one row per
  // date x category x segment, mirroring what the grid shows.
  const exportRowCount = effectiveDetail === "daywise" ? heatmap?.daywise?.length : heatmap?.rows?.length;

  /** Client-side CSV (UTF-8 BOM + CRLF), shaped like the grid on screen.
   *  Hourly: rows are dates, columns are time windows, plus the Total row.
   *  Day-wise: one row per date x category x segment, plus the range totals. */
  const exportCsv = () => {
    const toCsvCell = (v: string | number | null | undefined): string => {
      const str = v === null || v === undefined ? "" : String(v);
      return `"${str.replace(/"/g, '""')}"`;
    };
    const lines: string[] = [];

    if (effectiveDetail === "daywise") {
      const daywise = heatmap?.daywise ?? [];
      if (!daywise.length) {
        toast.warning("No data to download");
        return;
      }
      lines.push(["Date", "Day", "Category", "Segment", "Available", "Booked", "Conducted", "Conduct%", "Booked%"].map(toCsvCell).join(","));
      const push = (date: string, dow: string, cat: string, seg: string, m: MetricGroup | null | undefined) => {
        if (!m) return;
        lines.push([date, dow, cat, seg, m.available, m.booked, m.conducted, fmtPct(m.conductPct), fmtPct(m.bookedPct)].map(toCsvCell).join(","));
      };
      const pushCountries = (date: string, dow: string, cat: string, countries?: HeatmapCountries | null) => {
        if (!countries) return;
        push(date, dow, cat, "India", countries.india);
        push(date, dow, cat, "US", countries.us);
        push(date, dow, cat, "ROW", countries.row);
      };
      const pushGroup = (
        date: string,
        dow: string,
        cat: string,
        g: { total?: MetricGroup; intl?: MetricGroup; dom?: MetricGroup; countries?: HeatmapCountries | null },
      ) => {
        push(date, dow, cat, "Total", g.total);
        push(date, dow, cat, "Intl", g.intl);
        push(date, dow, cat, "Dom", g.dom);
        pushCountries(date, dow, cat, g.countries);
      };
      daywise.forEach((d) => {
        pushGroup(d.date, d.dow, "All courses", d);
        d.byCategory.forEach((c) => pushGroup(d.date, d.dow, c.categoryName, c));
      });
      // The Total row the grid shows above the dates.
      const overall = heatmap?.totals?.overall;
      if (overall) {
        const span = `${daywise.length} days`;
        pushGroup("Total", span, "All courses", {
          total: overall,
          intl: overall.intl,
          dom: overall.dom,
          countries: heatmap?.totals?.countries ?? overall.countries,
        });
        (heatmap?.totals?.byCategory ?? []).forEach((c) => pushGroup("Total", span, c.categoryName, c));
      }
    } else {
      const rows = heatmap?.rows ?? [];
      if (!rows.length) {
        toast.warning("No data to download");
        return;
      }
      const windows = heatmap?.timeWindows ?? [];

      if (metric === "aecount") {
        // Headcount mode: per-date AE/TE totals, then the per-window headcounts the grid shows.
        const headcounts = grid?.totalsPerWindow ?? [];
        lines.push(["Date", "Day", "Total", "AE", "TE", ...windows].map(toCsvCell).join(","));
        rows.forEach((r) => {
          const cells = windows.map((w) => headcounts.find((h) => h.window === w)?.teacherCount ?? "");
          lines.push([r.date, r.dow, r.teacherCount, r.aeCount, r.teCount, ...cells].map(toCsvCell).join(","));
        });
      } else {
        // Matrix mirroring the grid: rows are dates, columns are time windows, and each cell
        // carries the same two numbers the on-screen cell shows.
        const cellText = (c: HeatmapCell | undefined): string => {
          if (!c || c.available === 0) return "";
          return metric === "booked"
            ? `${c.booked}/${c.available} (${fmtPct(c.bookedPct)})`
            : `${c.conducted}/${c.booked} (${fmtPct(c.conductPct)})`;
        };
        lines.push(["Date", "Day", ...windows].map(toCsvCell).join(","));
        // The Total row the grid pins above the dates — skipped on a single day, where it
        // would just repeat the only data row.
        const perWindow = heatmap?.totals?.perWindow ?? [];
        if (rows.length > 1 && perWindow.length) {
          const cells = windows.map((w) => cellText(perWindow.find((c) => c.window === w)));
          lines.push(["TOTAL", `${rows.length} days`, ...cells].map(toCsvCell).join(","));
        }
        rows.forEach((r) => {
          const cells = windows.map((w) => cellText(r.cells.find((c) => c.window === w)));
          lines.push([r.date, r.dow, ...cells].map(toCsvCell).join(","));
        });
      }
    }

    const csvText = `﻿${lines.join("\r\n")}`;
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `slot-analysis_heatmap-${effectiveDetail}_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    trackEvent(MixpanelEvents.SLOT_ANALYSIS_CSV_EXPORT, { view: "heatmap", from, to, role, metric, granularity: effectiveDetail });
    toast.success("CSV downloaded");
  };

  const legend = LEGEND[metric];

  const modeOptions: { value: HmMode; label: string }[] = [
    { value: "category", label: "Category" },
    { value: "person", label: "Person" },
  ];
  const roleOptions: { value: DashboardRole; label: string }[] = [
    { value: "all", label: "All" },
    { value: "AE", label: "AE" },
    { value: "TE", label: "TE" },
  ];
  const detailOptions: { value: DashboardGranularity; label: string; disabled?: boolean; title?: string }[] = [
    { value: "hourly", label: "Hourly grid" },
    {
      value: "daywise",
      label: "Day-wise (condensed)",
      disabled: daywiseDisabled,
      title: daywiseDisabled ? "Day-wise view not available for AE/TE Count" : undefined,
    },
  ];
  const metricOptions: { value: HmMetric; label: string }[] = [
    { value: "booked", label: "Booked / Available" },
    { value: "conduct", label: "Conduct %" },
    { value: "aecount", label: "AE / TE Count" },
  ];
  const categoryOptions = [
    { value: "all", label: "All Categories" },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ];
  const teacherOptions = [
    { value: "all", label: "All people" },
    ...teachers.map((t) => ({ value: String(t.teacherId), label: `${t.name} (${t.role})` })),
  ];

  return (
    <div className="ds-stack sa-stack">
      {/* Filters */}
      <Card className="sa-filters">
        {/* Row 1 — data selection */}
        <Toolbar>
          <Field label="View" className="sa-toolbar__auto">
            {(ctl) => <SegmentedControl<HmMode> id={ctl.id} aria-label="View" options={modeOptions} value={mode} onChange={setMode} />}
          </Field>
          {mode === "category" ? (
            <Field label="Category">
              {(ctl) => <Select {...ctl} size="sm" options={categoryOptions} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} />}
            </Field>
          ) : (
            <Field label="Person">
              {(ctl) => <Select {...ctl} size="sm" options={teacherOptions} value={teacherId} onChange={(e) => setTeacherId(e.target.value)} />}
            </Field>
          )}
          <Field label="Role" className="sa-toolbar__auto">
            {(ctl) => <SegmentedControl<DashboardRole> id={ctl.id} aria-label="Role" options={roleOptions} value={role} onChange={setRole} />}
          </Field>
          <Field label="From">
            {(ctl) => <TextInput {...ctl} type="date" size="sm" value={from} onChange={(e) => setFrom(e.target.value)} />}
          </Field>
          <Field label="To">
            {(ctl) => <TextInput {...ctl} type="date" size="sm" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
          <div className="ds-toolbar__end">
            <Button size="sm" icon={<Download size={14} aria-hidden />} onClick={exportCsv} disabled={loading || !exportRowCount}>
              Export CSV
            </Button>
          </div>
        </Toolbar>

        {/* Row 2 — display options */}
        <Toolbar className="sa-filters__secondary">
          <Field label="Layout" className="sa-toolbar__auto">
            {(ctl) => (
              <SegmentedControl<DashboardGranularity>
                id={ctl.id}
                aria-label="Layout"
                options={detailOptions}
                value={effectiveDetail}
                onChange={setDetail}
              />
            )}
          </Field>
          <Field label="Show" className="sa-toolbar__auto">
            {(ctl) => <SegmentedControl<HmMetric> id={ctl.id} aria-label="Show" options={metricOptions} value={metric} onChange={setMetric} />}
          </Field>
        </Toolbar>
      </Card>

      {/* Collapsed daily summary — people × time-window grid */}
      {grid && (
        <CollapsedSummary
          people={people}
          timeWindows={grid.timeWindows}
          open={collapsedOpen}
          onToggle={() => setCollapsedOpen((o) => !o)}
          label={mode === "person" ? people[0]?.name || "Person" : "All people"}
          range={`${heatmap?.from ?? from} → ${heatmap?.to ?? to}`}
        />
      )}

      {/* Heatmap panel */}
      <Card
        flush
        title={title}
        subtitle={subtitle}
        actions={
          <div className="sa-legend">
            <div className="sa-legend__items">
              {legend.note ? (
                <span className="ds-text-subtle">{legend.note}</span>
              ) : (
                legend.swatches.map((s) => (
                  <span className="sa-legend__item" key={s.label}>
                    <span className={`sa-legend__dot ${s.cls}`} aria-hidden />
                    {s.label}
                  </span>
                ))
              )}
            </div>
            <div className="sa-legend__key ds-text-subtle">Cell: <span>{legend.cellKey}</span></div>
          </div>
        }
      >
        {loading ? (
          <Loader label="Loading slot heatmap…" />
        ) : error ? (
          <div className="sa-pad">
            <EmptyState title="Couldn't load the heatmap" description={error} />
          </div>
        ) : mode === "person" && teacherId !== "all" ? (
          <PersonMetricGrid person={people[0]} timeWindows={grid?.timeWindows ?? []} metric={metric} />
        ) : effectiveDetail === "hourly" ? (
          <HourlyGrid heatmap={heatmap} grid={grid} role={role} metric={metric} />
        ) : (
          <DaywiseGrid heatmap={heatmap} role={role} metric={metric} />
        )}
      </Card>
    </div>
  );
};

/* ── Collapsed daily summary: chips + expandable people × time-window slot grid ── */
const CollapsedSummary: React.FC<{
  people: TeacherGridRow[];
  timeWindows: string[];
  open: boolean;
  onToggle: () => void;
  label: string;
  range: string;
}> = ({ people, timeWindows, open, onToggle, label, range }) => {
  const hasSlot = (slot: string | null | undefined) => slot === "intl" || slot === "dom" || slot === "both";
  const totalWindows = people.reduce((s, p) => s + p.cells.filter((c) => hasSlot(c.slot)).length, 0);
  const aeCount = people.filter((p) => p.role === "AE").length;
  const teCount = people.filter((p) => p.role === "TE").length;
  const intlCount = people.reduce((s, p) => s + p.cells.filter((c) => c.slot === "intl" || c.slot === "both").length, 0);
  const domCount = people.reduce((s, p) => s + p.cells.filter((c) => c.slot === "dom" || c.slot === "both").length, 0);

  const slotAt = (p: TeacherGridRow, w: string) => p.cells.find((c) => c.window === w)?.slot ?? null;
  const countAt = (w: string) => people.filter((p) => hasSlot(slotAt(p, w))).length;

  return (
    <Disclosure
      open={open}
      onToggle={onToggle}
      summary={
        <div className="sa-summary">
          <span className="ds-strong">{label} · {range}</span>
          <span className="sa-summary__chips">
            <Badge tone="primary">{totalWindows} total slot-windows</Badge>
            {aeCount > 0 && <Badge tone="primary">AE {aeCount}</Badge>}
            {teCount > 0 && <Badge tone="success">TE {teCount}</Badge>}
            <Badge tone="info">✈ {intlCount} intl</Badge>
            <Badge tone="warning">⌂ {domCount} dom</Badge>
          </span>
        </div>
      }
    >
      <Table caption="Slot windows per person" stickyHeader wrapClassName="sa-people-scroll" className="sa-people-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Name</th>
            <th>Students</th>
            {timeWindows.map((t) => (
              <th key={t} className="sa-window-th">{t}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.teacherId} className={p.role === "AE" ? "sa-row--ae" : "sa-row--te"}>
              <td>{roleTag(p.role)}</td>
              <td>{p.name}</td>
              <td>{studentBadge(p.role, p.paidStudents)}</td>
              {timeWindows.map((w) => (
                <td key={w} className="sa-window-td">{slotChip(slotAt(p, w))}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="sa-total-label">Total slots / window</td>
            {timeWindows.map((w) => {
              const n = countAt(w);
              return <td key={w} className="sa-window-td ds-num sa-text--ae">{n || ""}</td>;
            })}
          </tr>
        </tfoot>
      </Table>
    </Disclosure>
  );
};

/* ── Person mode grid: one teacher, per-window own metrics (from teacher-grid cells[].metrics) ── */
const PersonMetricGrid: React.FC<{ person: TeacherGridRow | undefined; timeWindows: string[]; metric: HmMetric }> = ({ person, timeWindows, metric }) => {
  if (!person) {
    return (
      <div className="sa-pad">
        <EmptyState title="No data for this teacher" description="No data for this teacher in the selected range. Try a wider date range." />
      </div>
    );
  }
  // AE/TE Count is a supply-concentration measure across people — meaningless for a single person.
  const cellMetric: HmMetric = metric === "aecount" ? "booked" : metric;
  return (
    <div className="hm-grid">
      <table className="hm-table">
        <tbody>
          <tr>
            <th className="row-th" />
            {timeWindows.map((w) => <th key={w}>{w}</th>)}
          </tr>
          <tr>
            <td className="hm-row-label">
              <div>{person.name}</div>
              <div className="hm-row-sub">{person.role}</div>
            </td>
            {timeWindows.map((w) => {
              const cell = person.cells.find((c) => c.window === w);
              const m = cell?.metrics;
              if (!m || m.available === 0) {
                return <td key={w}><div className="hm-cell hc-empty" /></td>;
              }
              return (
                <HeatTip key={w} tip={
                  <>
                    <div className="sa-heat-tip__title">{person.name} · {w}</div>
                    <MetricLines m={m} />
                    <CountryBreakdown countries={cell?.countries} />
                  </>
                }>
                  <div className={`hm-cell ${metricCellClass(cellMetric, m)}`}>{cellBody(cellMetric, m)}</div>
                </HeatTip>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ── Hourly grid: rows=dates, cols=time windows ── */
const HourlyGrid: React.FC<{
  heatmap: HeatmapResponse | null;
  grid: TeacherGridResponse | null;
  role: DashboardRole;
  metric: HmMetric;
}> = ({ heatmap, grid, role, metric }) => {
  if (!heatmap || !heatmap.rows || !heatmap.rows.length) {
    return (
      <div className="sa-pad">
        <EmptyState title="No slot data for this range" description="Try a wider date range, another role or another category." />
      </div>
    );
  }
  const windows = heatmap.timeWindows;
  const rows = heatmap.rows;
  const perWindow = heatmap.totals?.perWindow ?? [];
  const overall = heatmap.totals?.overall;
  const isAeCount = metric === "aecount";
  const isSingleDay = rows.length === 1;

  // Headcount per window comes from the teacher grid (a person with an intl and a dom slot
  // in the same window counts once) — the aggregate cells carry slot counts, not people.
  const headcounts = grid?.totalsPerWindow ?? [];
  const maxHeadcount = Math.max(1, ...headcounts.map((h) => h.teacherCount));

  const renderMetricCell = (cell: HeatmapCell | undefined, heading: string, key: string, strong?: boolean) => {
    if (!cell || cell.available === 0) {
      return <td key={key}><div className={`hm-cell ${metric === "booked" ? "hb-empty" : "hc-empty"}`} /></td>;
    }
    return (
      <HeatTip key={key} tip={
        <>
          <div className="sa-heat-tip__title">{heading}</div>
          <MetricLines m={cell} withAeTe={{ ae: cell.ae, te: cell.te }} roleAll={role === "all"} />
          <CountryBreakdown countries={cell.countries} />
        </>
      }>
        <div className={`hm-cell ${metricCellClass(metric, cell)} ${strong ? "hm-cell-strong" : ""}`}>{cellBody(metric, cell)}</div>
      </HeatTip>
    );
  };

  const renderHeadcountCell = (w: string, dateLabel: string) => {
    const h = headcounts.find((x) => x.window === w);
    const n = h?.teacherCount ?? 0;
    return (
      <HeatTip key={`${dateLabel}-${w}`} tip={
        <>
          <div className="sa-heat-tip__title">{w} · {dateLabel}</div>
          <div className="sa-heat-tip__row"><span>Total available</span><b>{n}</b></div>
          <div className="sa-heat-tip__row"><span>AEs</span><b style={{ color: "#3d52d5" }}>{h?.aeCount ?? 0}</b></div>
          <div className="sa-heat-tip__row"><span>TEs</span><b style={{ color: "#0b8a6e" }}>{h?.teCount ?? 0}</b></div>
        </>
      }>
        <div className={`hm-cell ${aeCountCellClass(n, maxHeadcount)}`}>
          <div className="hm-cell-ratio">{n || "—"}</div>
        </div>
      </HeatTip>
    );
  };

  // Single-day, non-headcount: the range totals collapse to one day, so show them as a strip
  // instead of a totals row that would just repeat the only data row.
  const strip = !isAeCount && isSingleDay && overall && (
    <div className="sa-day-strip">
      <span className="sa-day-strip__label">Day total</span>
      <span className="ds-num sa-text--ae">{overall.available} <span className="ds-text-subtle">available</span></span>
      <span className="ds-num">{overall.booked} <span className="ds-text-subtle">booked</span></span>
      <Badge tone={bucketTone(overall.conductBucket)}>{fmtPct(overall.conductPct)} conduct</Badge>
    </div>
  );

  return (
    <>
      {strip}
      <div className="hm-grid">
        <table className="hm-table">
          <tbody>
            <tr>
              <th className="row-th">Date</th>
              <th className="row-th dow-th">Day</th>
              {isAeCount && <th className="row-th tot-th">Total</th>}
              {windows.map((w) => <th key={w}>{w}</th>)}
            </tr>

            {/* Totals row at top — headcount mode has its own per-day Total column instead. */}
            {!isAeCount && !isSingleDay && (
              <tr className="hm-totals-row">
                <td className="hm-row-label hm-row-total" colSpan={2}>Total</td>
                {windows.map((w) => renderMetricCell(perWindow.find((c) => c.window === w), `Total · ${w}`, `tot-${w}`, true))}
              </tr>
            )}

            {rows.map((r) => (
              <tr key={r.date}>
                <td className="hm-row-label">{r.date}</td>
                <td className="hm-row-label hm-row-dow">{r.dow}</td>
                {isAeCount && (
                  <td className="hm-daytotal-td">
                    <div className="hm-daytotal-cell">
                      <span className="hm-daytotal-n">{r.teacherCount}</span>
                      <span className="hm-daytotal-split">
                        <span className="sa-text--ae">{r.aeCount}ae</span>·<span className="sa-text--te">{r.teCount}te</span>
                      </span>
                    </div>
                  </td>
                )}
                {windows.map((w) =>
                  isAeCount
                    ? renderHeadcountCell(w, r.date)
                    : renderMetricCell(r.cells.find((c) => c.window === w), `${w} · ${r.date} (${r.dow})`, `${r.date}-${w}`),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

/* ── Day-wise condensed grid: rows=dates, Total/Intl/Dom per category + All ── */
const DaywiseGrid: React.FC<{
  heatmap: HeatmapResponse | null;
  role: DashboardRole;
  metric: HmMetric;
}> = ({ heatmap, role, metric }) => {
  if (!heatmap || !heatmap.daywise || !heatmap.daywise.length) {
    return (
      <div className="sa-pad">
        <EmptyState title="No day-wise data for this range" description="Try a wider date range or switch to the hourly grid." />
      </div>
    );
  }
  const daywise = heatmap.daywise;
  const presentCats = daywise[0]?.byCategory?.map((c) => ({ id: c.categoryId, name: c.categoryName })) ?? [];
  const splitByCourse = presentCats.length > 1;

  const metricCell = (
    m: MetricGroup | HeatmapCell | undefined,
    key: string,
    heading: string,
    countries?: HeatmapCountries | null,
    edge?: boolean,
    strong?: boolean,
  ) => {
    const tdClass = `dw-cell${edge ? " dw-edge" : ""}`;
    if (!m || m.available === 0) {
      return (
        <td key={key} className={tdClass}>
          <div className={`hm-cell dw-hm-cell ${metric === "booked" ? "hb-empty" : "hc-empty"}`} />
        </td>
      );
    }
    const cell = m as HeatmapCell;
    return (
      <HeatTip
        key={key}
        className={tdClass}
        tip={
          <>
            <div className="sa-heat-tip__title">{heading}</div>
            <MetricLines
              m={m}
              withAeTe={{ ae: cell.ae, te: cell.te }}
              roleAll={role === "all"}
            />
            <CountryBreakdown countries={countries ?? cell.countries} />
          </>
        }
      >
        <div className={`hm-cell dw-hm-cell ${metricCellClass(metric, m)} ${strong ? "hm-cell-strong" : ""}`}>
          {cellBody(metric, m)}
        </div>
      </HeatTip>
    );
  };

  // "All courses" leads the table: it is the number people read first, and trailing it after every
  // category pushed it past the horizontal scroll.
  const catCols = splitByCourse ? ["all", ...presentCats.map((c) => String(c.id))] : ["all"];

  // Range totals, from the server. Skipped on a single-day range, where the row would just
  // repeat the only data row.
  const overall = heatmap.totals?.overall;
  const totalsByCat = heatmap.totals?.byCategory ?? [];
  const showTotalsRow = daywise.length > 1 && !!overall;

  const segLabel = (seg: "Total" | "Intl" | "Dom", course: string) =>
    `${course} · ${seg}`;

  return (
    <Table caption="Day-wise slot metrics by course and segment" className="sa-daywise">
      <thead>
        <tr>
          <th rowSpan={2} className="dw-edge">Date</th>
          <th colSpan={3} className={`dw-group${splitByCourse ? " dw-edge" : ""}`}>
            {splitByCourse ? "All courses" : presentCats[0]?.name || "All"}
          </th>
          {splitByCourse &&
            presentCats.map((c) => (
              <th key={c.id} colSpan={3} className="dw-group">
                {c.name}
              </th>
            ))}
        </tr>
        <tr>
          {catCols.map((k) => (
            <React.Fragment key={k}>
              <th className="dw-seg">Σ Total</th>
              <th className="dw-seg sa-text--intl">✈ Intl</th>
              <th className={`dw-seg sa-text--dom${splitByCourse && k === "all" ? " dw-edge" : ""}`}>⌂ Dom</th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {showTotalsRow && (
          <tr className="dw-totals-row">
            <td className="dw-date dw-edge">
              <div className="ds-strong">Total</div>
              <div className="ds-text-subtle dw-date__day">{daywise.length} days</div>
            </td>
            {metricCell(overall, "tot-t-all", `${segLabel("Total", "All courses")} · ${daywise.length} days`, heatmap.totals?.countries ?? overall?.countries, false, true)}
            {metricCell(overall?.intl, "tot-i-all", `${segLabel("Intl", "All courses")} · ${daywise.length} days`, heatmap.totals?.countries ?? overall?.countries, false, true)}
            {metricCell(overall?.dom, "tot-d-all", `${segLabel("Dom", "All courses")} · ${daywise.length} days`, heatmap.totals?.countries ?? overall?.countries, splitByCourse, true)}
            {splitByCourse &&
              presentCats.map((c) => {
                const bc = totalsByCat.find((b) => b.categoryId === c.id);
                return (
                  <React.Fragment key={c.id}>
                    {metricCell(bc?.total, `tot-t-${c.id}`, `${segLabel("Total", c.name)} · ${daywise.length} days`, bc?.countries, false, true)}
                    {metricCell(bc?.intl, `tot-i-${c.id}`, `${segLabel("Intl", c.name)} · ${daywise.length} days`, bc?.countries, false, true)}
                    {metricCell(bc?.dom, `tot-d-${c.id}`, `${segLabel("Dom", c.name)} · ${daywise.length} days`, bc?.countries, false, true)}
                  </React.Fragment>
                );
              })}
          </tr>
        )}
        {daywise.map((d) => {
          const dateLabel = `${d.date} (${d.dow})`;
          const allCourse = splitByCourse ? "All courses" : presentCats[0]?.name || "All";
          return (
            <tr key={d.date}>
              <td className="dw-date dw-edge">
                <div className="ds-strong">{d.date}</div>
                <div className="ds-text-subtle dw-date__day">{d.dow}</div>
              </td>
              {metricCell(d.total, `${d.date}-t-all`, `${segLabel("Total", allCourse)} · ${dateLabel}`, d.countries)}
              {metricCell(d.intl, `${d.date}-i-all`, `${segLabel("Intl", allCourse)} · ${dateLabel}`, d.countries)}
              {metricCell(d.dom, `${d.date}-d-all`, `${segLabel("Dom", allCourse)} · ${dateLabel}`, d.countries, splitByCourse)}
              {splitByCourse &&
                presentCats.map((c) => {
                  const bc = d.byCategory.find((b) => b.categoryId === c.id);
                  return (
                    <React.Fragment key={c.id}>
                      {metricCell(bc?.total, `${d.date}-t-${c.id}`, `${segLabel("Total", c.name)} · ${dateLabel}`, bc?.countries)}
                      {metricCell(bc?.intl, `${d.date}-i-${c.id}`, `${segLabel("Intl", c.name)} · ${dateLabel}`, bc?.countries)}
                      {metricCell(bc?.dom, `${d.date}-d-${c.id}`, `${segLabel("Dom", c.name)} · ${dateLabel}`, bc?.countries)}
                    </React.Fragment>
                  );
                })}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default SlotHeatmap;
